import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link, useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';
import Preloader from '../../components/Preloader/Preloader';
import PasswordStrengthIndicator from "../../components/passwordStrengthIndicator";
import { Helmet } from 'react-helmet-async';

const isNumberRegx = /\d/;
const specialCharacterRegx = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
const capsRegx = /[A-Z ]+/;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [username, setUsername] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [nextPage, setNextPage] = useState(false);  
  const [loaded, setLoaded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [newpasswordFocused, setNewPasswordFocused] = useState(false);
  const [newpasswordValidity, setNewPasswordValidity] = useState({
    minChar: null,
    number: null,
    specialChar: null,
    capsChar: null
  });

  const handleSendAuthEmail = async (event) => {
    event.preventDefault();
    try {
      if (!username.trim()) {
        enqueueSnackbar("Please enter your registered email address", { variant: 'info' });
        return;
      }
      setLoading(true);
      setLoaded(false);
      const output = await resetPassword({ username: username.trim() });
      enqueueSnackbar("Password Reset initiated! Check your email.", { variant: 'info' });
      handleResetPasswordNextSteps(output);
      setLoaded(true);
      setLoading(false);
    } catch (error) {
      setLoaded(true);
      setLoading(false);
      enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
    }  
  };

  function handleResetPasswordNextSteps(output) {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        enqueueSnackbar(`Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium || 'your email'}`, { variant: 'info' });
        setNextPage(true);
        break;
      case 'DONE':
        enqueueSnackbar('Password Reset Successful! You can now sign in.', { variant: 'success' });
        navigate("/login");
        break;
      default:
        break;
    }
  }

  const handleConfirmResetPassword = async (event) => {
    event.preventDefault();
    try {
      if (!confirmationCode.trim()) {
        enqueueSnackbar("Enter the verification code sent to your email", { variant: 'info' });
        return;
      }
      if (!newPassword) {
        enqueueSnackbar("Please enter your new password", { variant: 'info' });
        return;
      }
      setLoading(true);
      setLoaded(false);

      await confirmResetPassword({ 
        username: username.trim(), 
        confirmationCode: confirmationCode.trim(), 
        newPassword 
      }).then(() => {
        enqueueSnackbar("Password changed successfully! Please log in with your new password.", { variant: 'success' });
        setLoaded(true);
        setLoading(false);
        navigate("/login");
      }).catch((error) => {
        setLoaded(true);
        setLoading(false);
        enqueueSnackbar("Verification failed: " + error.message, { variant: 'warning' });
      });
    } catch (error) {
      setLoaded(true);
      setLoading(false);
      enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
    }  
  };

  const onNewChangePassword = (pass) => {
    setNewPassword(pass);
    setNewPasswordValidity({
      minChar: pass.length >= 8,
      number: isNumberRegx.test(pass),
      specialChar: specialCharacterRegx.test(pass),
      capsChar: capsRegx.test(pass)
    });
  };

  return (
    <>
      <Helmet>
        <title>Reset Password - Student Portal - Vidyajyoti College of Theology</title>
        <meta name="description" content="Reset your Student Portal account password" />
        <link rel="canonical" href="/forgot-password" />
      </Helmet>
     
      <Preloader show={!loaded} />

      <div className="forgot-page-wrapper">
        <div className="card auth-card animate-scale-in">
          {/* Header */}
          <div className="text-center mb-4">
            <img
              src={logo}
              alt="College Crest"
              width="80"
              height="80"
              className="rounded-circle shadow-sm mb-2"
              style={{ border: '3px solid #1e3a8a', objectFit: 'cover' }}
            />
            <h3 className="fw-bold mb-1" style={{ color: '#1e3a8a', fontFamily: 'Outfit, sans-serif' }}>
              Reset Password
            </h3>
            <p className="text-muted small mb-0">
              {!nextPage 
                ? "Enter your registered student email address to receive a verification code."
                : "Enter the code sent to your email along with your new password."
              }
            </p>
          </div>

          <Form onSubmit={!nextPage ? handleSendAuthEmail : handleConfirmResetPassword}>
            <Form.Group className="mb-3" controlId="resetEmail">
              <Form.Label className="form-label small">
                <i className="fa fa-envelope-o me-1 text-primary"></i> Registered Email
              </Form.Label>
              <div className="auth-input-group">
                <i className="fa fa-envelope-o auth-icon-left"></i>
                <Form.Control 
                  type="email" 
                  placeholder="student@vidyajyoticollege.in"
                  autoFocus={!nextPage}
                  onChange={e => setUsername(e.target.value)}
                  value={username}
                  disabled={nextPage || loading}
                  required
                  className="auth-input"
                />
              </div>
            </Form.Group>

            {nextPage && (
              <>
                <Form.Group className="mb-3" controlId="resetCode">
                  <Form.Label className="form-label small">
                    <i className="fa fa-key me-1 text-warning"></i> Verification Code
                  </Form.Label>
                  <div className="auth-input-group">
                    <i className="fa fa-shield auth-icon-left"></i>
                    <Form.Control 
                      type="text" 
                      placeholder="Enter 6-digit verification code"
                      onChange={e => setConfirmationCode(e.target.value)}
                      value={confirmationCode}
                      disabled={loading}
                      required
                      className="auth-input"
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="resetNewPass">
                  <Form.Label className="form-label small">
                    <i className="fa fa-lock me-1 text-primary"></i> New Password
                  </Form.Label>
                  <div className="auth-input-group">
                    <i className="fa fa-key auth-icon-left"></i>
                    <Form.Control 
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new strong password"
                      onFocus={() => setNewPasswordFocused(true)}
                      onChange={e => onNewChangePassword(e.target.value)}            
                      value={newPassword}
                      disabled={loading}
                      required
                      className="auth-input"
                    />
                    <button
                      type="button"
                      className="auth-btn-right"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex="-1"
                    >
                      <i className={`fa ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </Form.Group>

                {newpasswordFocused && (
                  <div className="p-3 mb-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <PasswordStrengthIndicator validity={newpasswordValidity} />
                  </div>
                )}
              </>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="btn-primary w-100 mt-3 py-2 fw-bold shadow-sm"
              disabled={loading}
              style={{ height: '48px', fontSize: '1rem', borderRadius: '12px' }}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin me-2"></i> Processing...
                </>
              ) : !nextPage ? (
                <>
                  <i className="fa fa-paper-plane me-2"></i> Send Reset Code
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle me-2"></i> Update Password
                </>
              )}
            </Button>
          </Form>

          <div className="text-center pt-3 border-top mt-3">
            <Link to="/login" className="text-primary fw-bold text-decoration-none small">
              <i className="fa fa-arrow-left me-1"></i> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
